import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { paymentMethodsApi, type SavedPaymentMethod } from '../api/paymentMethodsApi';

/**
 * Query key factory for payment methods
 */
const paymentMethodKeys = {
  all: ['paymentMethods'] as const,
  list: () => [...paymentMethodKeys.all, 'list'] as const,
};

/**
 * Hook for fetching saved payment methods
 */
export const usePaymentMethods = () => {
  return useQuery<SavedPaymentMethod[], Error>({
    queryKey: paymentMethodKeys.list(),
    queryFn: () => paymentMethodsApi.list(),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000,
  });
};

/**
 * Hook for adding a new payment method
 */
export const useAddPaymentMethod = () => {
  const queryClient = useQueryClient();

  return useMutation<SavedPaymentMethod, Error, string>({
    mutationFn: (stripePaymentMethodId) => paymentMethodsApi.add(stripePaymentMethodId),
    onSuccess: (newMethod) => {
      // Add the new method to the cached list
      queryClient.setQueryData<SavedPaymentMethod[]>(
        paymentMethodKeys.list(),
        (oldData) => {
          if (!oldData) return [newMethod];
          // If new method is default, update others
          if (newMethod.isDefault) {
            return [...oldData.map((m) => ({ ...m, isDefault: false })), newMethod];
          }
          return [...oldData, newMethod];
        }
      );
    },
  });
};

/**
 * Hook for removing a payment method
 */
export const useRemovePaymentMethod = () => {
  const queryClient = useQueryClient();

  return useMutation<void, Error, string>({
    mutationFn: (id) => paymentMethodsApi.remove(id),
    onSuccess: (_, deletedId) => {
      // Remove the method from the cached list
      queryClient.setQueryData<SavedPaymentMethod[]>(
        paymentMethodKeys.list(),
        (oldData) => oldData?.filter((m) => m.id !== deletedId) ?? []
      );
    },
  });
};

/**
 * Hook for setting a payment method as default
 */
export const useSetDefaultPaymentMethod = () => {
  const queryClient = useQueryClient();

  return useMutation<void, Error, string>({
    mutationFn: (id) => paymentMethodsApi.setDefault(id),
    onSuccess: (_, defaultId) => {
      // Update the default status in the cached list
      queryClient.setQueryData<SavedPaymentMethod[]>(
        paymentMethodKeys.list(),
        (oldData) =>
          oldData?.map((m) => ({
            ...m,
            isDefault: m.id === defaultId,
          })) ?? []
      );
    },
  });
};

/**
 * Convenience hook combining all payment method operations
 */
export const usePaymentMethodActions = () => {
  const queryClient = useQueryClient();
  const addMutation = useAddPaymentMethod();
  const removeMutation = useRemovePaymentMethod();
  const setDefaultMutation = useSetDefaultPaymentMethod();

  return {
    add: addMutation.mutateAsync,
    remove: removeMutation.mutateAsync,
    setDefault: setDefaultMutation.mutateAsync,
    isAdding: addMutation.isPending,
    isRemoving: removeMutation.isPending,
    isSettingDefault: setDefaultMutation.isPending,
    refresh: () => queryClient.invalidateQueries({ queryKey: paymentMethodKeys.all }),
  };
};
